import { useTranslation } from "react-i18next"
import { ComponentProps, ReactNode, useMemo } from "react"
import { useParams } from "react-router"

import { useQueryClient } from "@tanstack/react-query"
import {
  CogsAllowedTypes,
  CogsSelectableKeys,
  DirectCostUnitCost,
  DirectCostUnitCostConfig,
} from "../api/types"
import { useGetDataGroupedByYear } from "@/lib/hooks/useGroupData"
import { usePageContext } from "@/lib/context/page"
import { TableErrorProvider } from "@/feature/financial-model/ecommerce/revenues/context/TableErrorContext"
import { useCogsStore } from "../store"
import { Table, TableBody } from "@/components/ui/table"
import { useFormatLocalizedNumber } from "@/lib/hooks/useLocaleFormatter"
import { BadgesByScenario, CogsBadge, CogsBadgeOptions } from "./CogsBadges"
import {
  postDirectCostsPeriodicData,
  useGetCogsDirectCostsCategories,
  useGetDirectCostsPeriodicData,
  useSuspenseUnitsSettings,
  useSwitchCogsColumnType,
} from "../api"
import { Container } from "@/components/shared/Container"
import { CogsInputCell } from "./direct-costs-input-cells.tsx"
import {
  GenericPeriodTableHeader,
  InputTypeProviderProvider,
  InputTypeSelector,
  PeriodTableRow,
} from "@/components/shared/tables"
import { PeriodType } from "@/types"
import {
  Accordion,
  AccordionContent,
  AccordionTrigger,
  AccordionItem,
} from "@/components/ui/accordion"
import {
  NavigationProvider,
  useNavigationContext,
  YearNavigation,
} from "@/lib/context/navigation"
import { COGS_BASE_KEY, createQueryKey, TOTALS_KEY } from "@/api/query-keys.ts"
import {
  ActionablePeriodRowCell,
  ActionableRowFeedbackDialog,
  TableActionableRowProvider,
} from "@/components/shared/tables/actionable-row.tsx"
import { Show } from "@/components/shared/show"
import { useFinancialModelSettingsStore } from "@/feature/financial-model/settings/store/financialModelSettingsStore"
import {
  TableContextValue,
  TableProvider,
} from "@/components/shared/tables/table-context.tsx"
import {
  PeriodCellContent,
  PeriodFixedTableCell,
  PeriodRowHeader,
  PeriodTableCell,
} from "@/components/shared/tables/table-cell.tsx"

const SCENARIOS_MAP = {
  "1_aggregate_absolute": 1,
  "2_aggregate_cost_per_unit": 2,
  "4_aggregate_percentage_of_revenues": 4,
  "3_byproduct": 3,
} as const

const useCogsInputRowActionableContextProps = ({
  currentData,
  mutateDataKey,
  financialModelId,
}: {
  currentData: DirectCostUnitCost[]
  financialModelId: string
  mutateDataKey: CogsSelectableKeys
}) => {
  const queryClient = useQueryClient()

  return useMemo(
    () =>
      ({
        currentData,
        mutateDataKey,
        rowUpdateMutationArgs: {
          mutationKey: createQueryKey({
            baseKey: COGS_BASE_KEY,
            keys: [`direct-costs`, financialModelId],
          }),
          mutationItemFn: (item: DirectCostUnitCost, { rowType }) =>
            postDirectCostsPeriodicData({
              financialModelId,
              data: {
                ...item,
                config: {
                  ...item.config,
                  [mutateDataKey]: {
                    type: rowType,
                    allowed_types:
                      item.config[mutateDataKey]?.allowed_types ?? [],
                  },
                },
              },
            }),
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: createQueryKey({
                baseKey: COGS_BASE_KEY,
                keys: [`direct-costs`, financialModelId],
              }),
            })
            queryClient.resetQueries({
              queryKey: createQueryKey({
                baseKey: TOTALS_KEY,
                keys: [financialModelId, `direct-costs`],
              }),
            })
          },
        },
      }) as Omit<
        ComponentProps<typeof TableActionableRowProvider<DirectCostUnitCost>>,
        "children"
      >,
    [currentData, mutateDataKey, financialModelId, queryClient]
  )
}

// const HistoricalScenarioCostPerUnitRowInputCells = ({
//   financialModelId,
//   periods,
// }: {
//   financialModelId: string
//   periods: DirectCostUnitCost[]
// }) => {
//   const actionableRowContextProps = useCogsInputRowActionableContextProps({
//     currentData: periods,
//     financialModelId,
//     mutateDataKey: `cost_per_unit`,
//   })

//   return (
//     <TableActionableRowProvider {...actionableRowContextProps}>
//       <ActionableRowFeedbackDialog>
//         {periods.map((period, index) => (
//           <ActionableRowCell key={period.period} cellIndex={index}>
//             <TableCellContent className="w-full">
//               <CogsInputCell
//                 key={period.cost_per_unit ?? 0}
//                 period={period}
//                 selectedKey="cost_per_unit"
//               />
//             </TableCellContent>
//           </ActionableRowCell>
//         ))}
//       </ActionableRowFeedbackDialog>
//     </TableActionableRowProvider>
//   )
// }
// const HistoricalScenarioPercentageOfRevenuesRowInputCells = ({
//   financialModelId,
//   periods,
// }: {
//   financialModelId: string
//   periods: DirectCostUnitCost[]
// }) => {
//   const actionableRowContextProps = useCogsInputRowActionableContextProps({
//     currentData: periods,
//     financialModelId,
//     mutateDataKey: `percentage_of_revenues`,
//   })

//   return (
//     <TableActionableRowProvider {...actionableRowContextProps}>
//       <ActionableRowFeedbackDialog>
//         {periods.map((period, index) => (
//           <ActionableRowCell key={period.period} cellIndex={index}>
//             <TableCellContent className="w-full">
//               <CogsInputCell
//                 key={period.percentage_of_revenues ?? 0}
//                 period={period}
//                 selectedKey="percentage_of_revenues"
//               />
//             </TableCellContent>
//           </ActionableRowCell>
//         ))}
//       </ActionableRowFeedbackDialog>
//     </TableActionableRowProvider>
//   )
// }

const CopyAndPasteRow = ({
  financialModelId,
  periods,
  selectedKey,
  children,
}: {
  financialModelId: string
  periods: DirectCostUnitCost[]
  selectedKey: CogsSelectableKeys
  children: ReactNode
}) => {
  const actionableRowContextProps = useCogsInputRowActionableContextProps({
    currentData: periods,
    financialModelId,
    mutateDataKey: selectedKey,
  })

  return (
    <TableActionableRowProvider {...actionableRowContextProps}>
      <ActionableRowFeedbackDialog>{children}</ActionableRowFeedbackDialog>
    </TableActionableRowProvider>
  )
}

const HistoricalScenarioRows = ({
  periods,
}: {
  periods: DirectCostUnitCost[]
}) => {
  const { t } = useTranslation(`cogs`)
  const financialModelId = periods[0].financial_model_id
  const financialModelSettings = useFinancialModelSettingsStore(
    (settings) => settings.settings[financialModelId!]
  )
  const modelingApproach = useMemo(
    () => periods[0].modeling_approach,
    [periods]
  )

  const formatNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency,
  })

  const { state } = usePageContext()

  switch (modelingApproach) {
    case `absolute_value`:
      return (
        <PeriodTableRow type="output">
          <PeriodFixedTableCell>
            <PeriodRowHeader>
              {t(`cogs.periods.direct_costs.table.rows.revenues`)}
            </PeriodRowHeader>
          </PeriodFixedTableCell>
          {periods.map((period) => (
            <PeriodTableCell key={period.period} cell_period={period.period}>
              <PeriodCellContent>
                {formatNumber(period.revenue) ?? `--`}
              </PeriodCellContent>
            </PeriodTableCell>
          ))}
        </PeriodTableRow>
      )
    case `cost_per_unit`:
      return (
        <>
          <PeriodTableRow type="output">
            <PeriodFixedTableCell>
              <PeriodRowHeader>
                {t(`cogs.periods.direct_costs.table.rows.total_units`)}
              </PeriodRowHeader>
            </PeriodFixedTableCell>
            {periods.map((period) => (
              <PeriodTableCell key={period.period} cell_period={period.period}>
                <PeriodCellContent>
                  {formatNumber(period.total_applicable_units, {
                    style: `decimal`,
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  }) ?? `--`}
                </PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>

          <PeriodTableRow type="output">
            <PeriodFixedTableCell>
              <PeriodRowHeader>
                {t(
                  `cogs.periods.direct_costs.table.rows.average_price_per_unit`
                )}
              </PeriodRowHeader>
            </PeriodFixedTableCell>
            {periods.map((period) => (
              <PeriodTableCell key={period.period} cell_period={period.period}>
                <PeriodCellContent>
                  {formatNumber(period.average_price_per_unit) ?? `--`}
                </PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>
          <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
            <PeriodFixedTableCell>
              <PeriodRowHeader>
                {t(`cogs.periods.direct_costs.table.rows.cost_per_unit`)}
              </PeriodRowHeader>
            </PeriodFixedTableCell>
            <CopyAndPasteRow
              financialModelId={financialModelId}
              periods={periods}
              selectedKey="cost_per_unit"
            >
              {periods.map((period, index) => (
                <ActionablePeriodRowCell
                  key={period.period}
                  cellIndex={index}
                  cell_period={period.period}
                >
                  <PeriodCellContent>
                    <CogsInputCell
                      key={period.cost_per_unit ?? 0}
                      period={period}
                      selectedKey="cost_per_unit"
                    />
                  </PeriodCellContent>
                </ActionablePeriodRowCell>
              ))}
            </CopyAndPasteRow>
          </PeriodTableRow>
        </>
      )
    case `percentage_of_revenues`:
      return (
        <>
          <PeriodTableRow type="output">
            <PeriodFixedTableCell>
              <PeriodRowHeader>
                {t(`cogs.periods.direct_costs.table.rows.revenues`)}
              </PeriodRowHeader>
            </PeriodFixedTableCell>
            {periods.map((period) => (
              <PeriodTableCell key={period.period} cell_period={period.period}>
                <PeriodCellContent>
                  {formatNumber(period.revenue) ?? `--`}
                </PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>
          <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
            <PeriodFixedTableCell>
              <PeriodRowHeader>
                {t(
                  `cogs.periods.direct_costs.table.rows.percentage_of_revenues`
                )}
              </PeriodRowHeader>
            </PeriodFixedTableCell>
            <CopyAndPasteRow
              financialModelId={financialModelId}
              periods={periods}
              selectedKey="percentage_of_revs"
            >
              {periods.map((period, index) => (
                <ActionablePeriodRowCell
                  key={period.period}
                  cellIndex={index}
                  cell_period={period.period}
                >
                  <PeriodCellContent>
                    <CogsInputCell
                      key={period.percentage_revenues ?? 0}
                      period={period}
                      selectedKey="percentage_of_revs"
                    />
                  </PeriodCellContent>
                </ActionablePeriodRowCell>
              ))}
            </CopyAndPasteRow>
          </PeriodTableRow>
        </>
      )
    default:
      return null
  }
}

const ForecastScenarioRows = ({
  periods,
  config,
}: {
  periods: DirectCostUnitCost[]
  config: DirectCostUnitCostConfig
}) => {
  const { t } = useTranslation(`cogs`)
  const { state } = usePageContext()
  const { id: financialModelId } = useParams<{ id: string }>()
  const financialModelSettings = useFinancialModelSettingsStore(
    (settings) => settings.settings[financialModelId!]
  )
  const modelingApproach = useMemo(
    () => periods[0].modeling_approach,
    [periods]
  )
  const formatNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency ?? `EUR`,
  })
  const { isPending: isSwitchingColumnType, mutate: switchCogsColumnType } =
    useSwitchCogsColumnType(financialModelId!)
  const productID = periods[0].product_id
  const isDisabled = state.view !== `monthly` || isSwitchingColumnType

  switch (modelingApproach) {
    case `absolute_value`:
      return (
        <InputTypeProviderProvider defaultType={`absolute`}>
          <PeriodTableRow type="output">
            <PeriodFixedTableCell>
              <PeriodRowHeader>
                {t(`cogs.periods.direct_costs.table.rows.revenues`)}
              </PeriodRowHeader>
              <InputTypeSelector disabled allowedTypes={[`absolute`]} />
            </PeriodFixedTableCell>
            {periods.map((period) => (
              <PeriodTableCell
                key={period.period}
                cell_period={period.period}
                readonly={
                  state.queryPeriod === `all` &&
                  state.period !== period.period_type
                }
              >
                <PeriodCellContent>
                  {formatNumber(period.revenue) ?? `--`}
                </PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>
        </InputTypeProviderProvider>
      )
    case `cost_per_unit`:
      return (
        <>
          <InputTypeProviderProvider defaultType={`absolute`}>
            <PeriodTableRow type="output">
              <PeriodFixedTableCell>
                <PeriodRowHeader>
                  {t(`cogs.periods.direct_costs.table.rows.total_units`)}
                </PeriodRowHeader>
                <InputTypeSelector disabled allowedTypes={[`absolute`]} />
              </PeriodFixedTableCell>
              {periods.map((period) => (
                <PeriodTableCell
                  key={period.period}
                  cell_period={period.period}
                  readonly={
                    state.queryPeriod === `all` &&
                    state.period !== period.period_type
                  }
                >
                  <PeriodCellContent>
                    {formatNumber(period.total_applicable_units, {
                      style: `decimal`,
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    }) ?? `--`}
                  </PeriodCellContent>
                </PeriodTableCell>
              ))}
            </PeriodTableRow>
          </InputTypeProviderProvider>
          <InputTypeProviderProvider defaultType={`absolute`}>
            <PeriodTableRow type="output">
              <PeriodFixedTableCell>
                <PeriodRowHeader>
                  {t(
                    `cogs.periods.direct_costs.table.rows.average_price_per_unit`
                  )}
                </PeriodRowHeader>
                <InputTypeSelector disabled allowedTypes={[`absolute`]} />
              </PeriodFixedTableCell>
              {periods.map((period) => (
                <PeriodTableCell
                  key={period.period}
                  cell_period={period.period}
                  readonly={
                    state.queryPeriod === `all` &&
                    state.period !== period.period_type
                  }
                >
                  <PeriodCellContent type={`output`}>
                    {formatNumber(period.average_price_per_unit) ?? `--`}
                  </PeriodCellContent>
                </PeriodTableCell>
              ))}
            </PeriodTableRow>
          </InputTypeProviderProvider>
          <InputTypeProviderProvider defaultType={config.cost_per_unit.type}>
            <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
              <PeriodFixedTableCell>
                <PeriodRowHeader>
                  {t(`cogs.periods.direct_costs.table.rows.cost_per_unit`)}
                </PeriodRowHeader>
                <InputTypeSelector
                  disabled={isDisabled}
                  editable={state.view === `monthly`}
                  allowedTypes={config.cost_per_unit.allowed_types}
                  onTypeChange={(newType) => {
                    switchCogsColumnType({
                      column_name: `cost_per_unit`,
                      col_type: newType as CogsAllowedTypes,
                      period_type: state.period,
                      product_id: productID,
                    })
                  }}
                />
              </PeriodFixedTableCell>
              <CopyAndPasteRow
                financialModelId={financialModelId!}
                periods={periods}
                selectedKey="cost_per_unit"
              >
                {periods.map((period, index) => (
                  <ActionablePeriodRowCell
                    key={period.period}
                    cellIndex={index}
                    cell_period={period.period}
                    readonly={
                      state.queryPeriod === `all` &&
                      state.period !== period.period_type
                    }
                  >
                    <PeriodCellContent
                      type={
                        (state.queryPeriod === `all` &&
                          state.period !== period.period_type) ||
                        state.view === `yearly`
                          ? `output`
                          : `input`
                      }
                    >
                      <CogsInputCell
                        period={period}
                        selectedKey="cost_per_unit"
                      />
                    </PeriodCellContent>
                  </ActionablePeriodRowCell>
                ))}
              </CopyAndPasteRow>
            </PeriodTableRow>
          </InputTypeProviderProvider>
        </>
      )
    case `percentage_of_revenues`:
      return (
        <>
          <InputTypeProviderProvider defaultType={`absolute`}>
            <PeriodTableRow type="output">
              <PeriodFixedTableCell>
                <PeriodRowHeader>
                  {t(`cogs.periods.direct_costs.table.rows.revenues`)}
                </PeriodRowHeader>
                <InputTypeSelector disabled allowedTypes={[`absolute`]} />
              </PeriodFixedTableCell>
              {periods.map((period) => (
                <PeriodTableCell
                  key={period.period}
                  cell_period={period.period}
                  readonly={
                    state.queryPeriod === `all` &&
                    state.period !== period.period_type
                  }
                >
                  <PeriodCellContent>
                    {formatNumber(period.revenue) ?? `--`}
                  </PeriodCellContent>
                </PeriodTableCell>
              ))}
            </PeriodTableRow>
          </InputTypeProviderProvider>
          <InputTypeProviderProvider
            defaultType={config.percentage_of_revs.type}
          >
            <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
              <PeriodFixedTableCell>
                <PeriodRowHeader>
                  {t(
                    `cogs.periods.direct_costs.table.rows.percentage_of_revenues`
                  )}
                </PeriodRowHeader>
                <InputTypeSelector
                  disabled={isDisabled}
                  editable={state.view === `monthly`}
                  allowedTypes={config.percentage_of_revs.allowed_types}
                  onTypeChange={(newType) => {
                    switchCogsColumnType({
                      column_name: `percentage_of_revs`,
                      col_type: newType as CogsAllowedTypes,
                      period_type: state.period,
                      product_id: productID,
                    })
                  }}
                />
              </PeriodFixedTableCell>
              <CopyAndPasteRow
                financialModelId={financialModelId!}
                periods={periods}
                selectedKey="percentage_of_revs"
              >
                {periods.map((period, index) => (
                  <ActionablePeriodRowCell
                    key={period.period}
                    cellIndex={index}
                    cell_period={period.period}
                    readonly={
                      state.queryPeriod === `all` &&
                      state.period !== period.period_type
                    }
                  >
                    <PeriodCellContent
                      type={
                        (state.queryPeriod === `all` &&
                          state.period !== period.period_type) ||
                        state.view === `yearly`
                          ? `output`
                          : `input`
                      }
                    >
                      <CogsInputCell
                        period={period}
                        selectedKey="percentage_of_revs"
                      />
                    </PeriodCellContent>
                  </ActionablePeriodRowCell>
                ))}
              </CopyAndPasteRow>
            </PeriodTableRow>
          </InputTypeProviderProvider>
        </>
      )
    default:
      return null
  }
}

const ScenarioRows = ({
  periods,
  config,
}: {
  periods: DirectCostUnitCost[]
  config?: DirectCostUnitCostConfig
}) => {
  const { period } = useParams<{ period: PeriodType }>()

  switch (period) {
    case `historical`:
      return <HistoricalScenarioRows periods={periods} />
    case `forecast`:
      return <ForecastScenarioRows periods={periods} config={config!} />
    default:
      return null
  }
}

export const HistoricalDataTable = ({
  periods,
  categoryId,
  variant,
}: {
  periods: DirectCostUnitCost[]
  categoryId?: string
  variant: "base" | "accordion-item"
}) => {
  const { t } = useTranslation(`cogs`)
  const financialModelId = periods[0].financial_model_id
  const { years, months, availableYears, lastHistoricalPeriod } =
    useGetDataGroupedByYear<DirectCostUnitCost>(periods)
  const { state } = usePageContext()
  const { tableRef } = useNavigationContext()
  const modelingApproach = periods[0].modeling_approach
  const { data: categoryUnitSettings } = useSuspenseUnitsSettings(
    financialModelId!,
    categoryId
  )
  const {
    data: { categories },
  } = useGetCogsDirectCostsCategories(financialModelId)

  const contentTitle =
    categoryId && variant === `base`
      ? (categories.find((c) => c.id === categoryId)?.name ??
        `Unnamed Category`)
      : periods[0].product_name

  const financialModelSettings = useFinancialModelSettingsStore(
    (settings) => settings.settings[financialModelId!]
  )
  const formatLocalizedNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency,
  })

  const ItemContent = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionItem
    }
    return Container
  }, [variant])

  const Trigger = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionTrigger
    }
    return `div`
  }, [variant])

  const Content = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionContent
    }
    return `div`
  }, [variant])

  const tableContext = useMemo(
    () =>
      ({
        lastHistoricalPeriod,
        view: state.view,
        queryPeriod: state.queryPeriod,
        period: state.period,
      }) satisfies TableContextValue,
    [lastHistoricalPeriod, state.view, state.queryPeriod, state.period]
  )
  return (
    <div className="flex flex-col gap-6 mt-4">
      <ItemContent
        value={String(periods[0].product_id!)}
        className="bg-white px-4 shadow-loose rounded-lg"
      >
        <Trigger className="group hover:no-underline">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium group-hover:underline transition-all duration-300">
              {contentTitle}
            </h3>
            <Show
              when={!!categoryId && !!categoryUnitSettings.level_of_granularity}
            >
              <CogsBadge
                type={categoryUnitSettings.level_of_granularity!}
                size="md"
                variant="info"
              />
              {modelingApproach && (
                <CogsBadge
                  size="md"
                  variant="info"
                  type={modelingApproach as CogsBadgeOptions}
                />
              )}
            </Show>
            <Show when={!categoryId && variant === `accordion-item`}>
              <CogsBadge
                size="md"
                variant="info"
                type={modelingApproach as CogsBadgeOptions}
              />
            </Show>
          </div>
        </Trigger>
        <Content>
          <TableProvider value={tableContext}>
            <TableErrorProvider>
              <YearNavigation
                view={state.view}
                years={availableYears}
                className="mt-6"
              />
              <Table className="overflow-auto mt-6" ref={tableRef}>
                <GenericPeriodTableHeader
                  view={state.view}
                  years={years}
                  months={months}
                />
                <TableBody>
                  <ScenarioRows periods={periods} />
                  <PeriodTableRow
                    type={
                      state.view === `yearly` ||
                      modelingApproach !== `absolute_value`
                        ? `totals`
                        : `total-input`
                    }
                  >
                    <PeriodFixedTableCell>
                      <PeriodRowHeader direction="horizontal">
                        {t(`cogs.periods.direct_costs.table.rows.direct_costs`)}
                      </PeriodRowHeader>
                    </PeriodFixedTableCell>
                    <Show
                      when={modelingApproach === `absolute_value`}
                      fallback={periods.map((period) => (
                        <PeriodTableCell
                          key={period.period}
                          cell_period={period.period}
                        >
                          <PeriodCellContent type={`output`}>
                            <span>
                              {formatLocalizedNumber(period.direct_cost) ??
                                `--`}
                            </span>
                          </PeriodCellContent>
                        </PeriodTableCell>
                      ))}
                    >
                      <CopyAndPasteRow
                        financialModelId={financialModelId}
                        periods={periods}
                        selectedKey="direct_cost"
                      >
                        {periods.map((period, index) => (
                          <ActionablePeriodRowCell
                            key={period.period}
                            cellIndex={index}
                            cell_period={period.period}
                            readonly={
                              state.queryPeriod === `all` &&
                              state.period !== period.period_type
                            }
                          >
                            <PeriodCellContent
                              type={
                                (state.queryPeriod === `all` &&
                                  state.period !== period.period_type) ||
                                state.view === `yearly`
                                  ? `output`
                                  : `input`
                              }
                            >
                              <CogsInputCell
                                key={period.direct_cost ?? 0}
                                period={period}
                                selectedKey="direct_cost"
                                categoryId={categoryId}
                              />
                            </PeriodCellContent>
                          </ActionablePeriodRowCell>
                        ))}
                      </CopyAndPasteRow>
                    </Show>
                  </PeriodTableRow>
                </TableBody>
              </Table>
            </TableErrorProvider>
          </TableProvider>
        </Content>
      </ItemContent>
    </div>
  )
}

export const ForecastDataTable = ({
  periods,
  config,
  categoryId,
  variant,
}: {
  periods: DirectCostUnitCost[]
  config: DirectCostUnitCostConfig
  categoryId?: string
  variant: "base" | "accordion-item"
}) => {
  const { id: financialModelId } = useParams<{ id: string }>()
  const { mutate: switchCogsColumnType, isPending: isSwitchingColumnType } =
    useSwitchCogsColumnType(financialModelId!)
  const { t } = useTranslation(`cogs`)
  const { years, months, lastHistoricalPeriod, availableYears } =
    useGetDataGroupedByYear<DirectCostUnitCost>(periods)
  const { state } = usePageContext()

  const { tableRef } = useNavigationContext()
  const productID = periods[0].product_id
  const modelingApproach = periods[0].modeling_approach

  const { data: categoryUnitSettings } = useSuspenseUnitsSettings(
    financialModelId!,
    categoryId
  )
  const {
    data: { categories },
  } = useGetCogsDirectCostsCategories(financialModelId!)

  const contentTitle =
    categoryId && variant === `base`
      ? (categories.find((c) => c.id === categoryId)?.name ??
        `Unnamed Category`)
      : periods[0].product_name

  const ItemContent = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionItem
    }
    return Container
  }, [variant])

  const Trigger = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionTrigger
    }
    return `div`
  }, [variant])

  const Content = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionContent
    }
    return `div`
  }, [variant])

  const financialModelSettings = useFinancialModelSettingsStore(
    (settings) => settings.settings[financialModelId!]
  )
  const formatLocalizedNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency,
  })

  const tableContext = useMemo(
    () =>
      ({
        lastHistoricalPeriod,
        view: state.view,
        queryPeriod: state.queryPeriod,
        period: state.period,
      }) satisfies TableContextValue,
    [lastHistoricalPeriod, state.view, state.queryPeriod, state.period]
  )

  return (
    <div className="flex flex-col gap-6 mt-4">
      <ItemContent
        value={String(productID!)}
        className="bg-white px-4 shadow-loose rounded-lg"
      >
        <Trigger>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">{contentTitle}</h3>
            <Show
              when={!!categoryId && !!categoryUnitSettings.level_of_granularity}
            >
              <CogsBadge
                type={categoryUnitSettings.level_of_granularity!}
                size="md"
                variant="info"
              />
              {modelingApproach && (
                <CogsBadge
                  size="md"
                  variant="info"
                  type={modelingApproach as CogsBadgeOptions}
                />
              )}
            </Show>
            <Show when={!categoryId && variant === `accordion-item`}>
              <CogsBadge
                size="md"
                variant="info"
                type={modelingApproach as CogsBadgeOptions}
              />
            </Show>
          </div>
        </Trigger>
        <Content>
          <TableProvider value={tableContext}>
            <TableErrorProvider>
              <YearNavigation
                view={state.view}
                years={availableYears}
                className="mt-6"
              />
              <Table className="overflow-auto mt-6" ref={tableRef}>
                <GenericPeriodTableHeader
                  view={state.view}
                  years={years}
                  months={months}
                />
                <TableBody>
                  <ScenarioRows periods={periods} config={config} />
                  <InputTypeProviderProvider
                    defaultType={config.direct_cost.type}
                  >
                    <PeriodTableRow
                      type={
                        state.view === `yearly` ||
                        modelingApproach !== `absolute_value`
                          ? `totals`
                          : `total-input`
                      }
                    >
                      <PeriodFixedTableCell>
                        <PeriodRowHeader direction="horizontal" type="output">
                          {t(
                            `cogs.periods.direct_costs.table.rows.direct_costs`
                          )}
                        </PeriodRowHeader>
                        <InputTypeSelector
                          disabled={
                            state.view !== `monthly` ||
                            isSwitchingColumnType ||
                            modelingApproach === `cost_per_unit`
                          }
                          editable={
                            state.view === `monthly` &&
                            modelingApproach !== `cost_per_unit`
                          }
                          allowedTypes={config.direct_cost.allowed_types}
                          onTypeChange={(newType) =>
                            switchCogsColumnType({
                              column_name: `direct_cost`,
                              col_type: newType as CogsAllowedTypes,
                              period_type: state.period,
                              product_id: productID,
                            })
                          }
                        />
                      </PeriodFixedTableCell>
                      <Show
                        when={modelingApproach === `absolute_value`}
                        fallback={periods.map((period) => (
                          <PeriodTableCell
                            key={period.period}
                            cell_period={period.period}
                          >
                            <PeriodCellContent type={`output`}>
                              {formatLocalizedNumber(period.direct_cost) ??
                                `--`}
                            </PeriodCellContent>
                          </PeriodTableCell>
                        ))}
                      >
                        <CopyAndPasteRow
                          financialModelId={financialModelId!}
                          periods={periods}
                          selectedKey="direct_cost"
                        >
                          {periods.map((period, index) => (
                            <ActionablePeriodRowCell
                              key={period.period}
                              cellIndex={index}
                              cell_period={period.period}
                            >
                              <PeriodCellContent>
                                <CogsInputCell
                                  key={period.direct_cost ?? 0}
                                  period={period}
                                  categoryId={categoryId}
                                  selectedKey="direct_cost"
                                />
                              </PeriodCellContent>
                            </ActionablePeriodRowCell>
                          ))}
                        </CopyAndPasteRow>
                      </Show>
                    </PeriodTableRow>
                  </InputTypeProviderProvider>
                </TableBody>
              </Table>
            </TableErrorProvider>
          </TableProvider>
        </Content>
      </ItemContent>
    </div>
  )
}

export const CogsTable = ({ categoryId }: { categoryId?: string }) => {
  const { state } = usePageContext()
  const { id: financialModelId } = useParams<{ id: string }>()
  const { data } = useGetDirectCostsPeriodicData({
    financialModelId: financialModelId!,
    period_type: state.queryPeriod,
    view: state.view,
    categoryId,
  })
  const { scenario } = useCogsStore()
  switch (state.period) {
    case `historical`:
      if (scenario === SCENARIOS_MAP[`3_byproduct`]) {
        return (
          <div className="">
            <BadgesByScenario scenario={scenario} />
            <Accordion
              type="multiple"
              defaultValue={data.map(
                (periods) => periods?.periods[0]?.product_id!
              )}
            >
              {data.map((periods, i) => (
                <NavigationProvider
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                >
                  <HistoricalDataTable
                    periods={periods?.periods ?? []}
                    variant={`accordion-item`}
                  />
                </NavigationProvider>
              ))}
            </Accordion>
          </div>
        )
      }
      return (
        <div className="">
          <BadgesByScenario scenario={scenario} />
          {data.map((periods, i) => (
            <NavigationProvider
              // eslint-disable-next-line react/no-array-index-key
              key={i}
            >
              <HistoricalDataTable
                periods={periods?.periods ?? []}
                variant={`base`}
              />
            </NavigationProvider>
          ))}
        </div>
      )
    case `forecast`:
      if (scenario === SCENARIOS_MAP[`3_byproduct`]) {
        return (
          <div className="">
            <BadgesByScenario scenario={scenario} />
            <Accordion
              type="multiple"
              defaultValue={data.map(
                (periods) => periods?.periods[0]?.product_id!
              )}
            >
              {data.map((periods, i) => (
                <NavigationProvider
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                >
                  <ForecastDataTable
                    variant={`accordion-item`}
                    periods={periods?.periods ?? []}
                    config={periods?.config ?? {}}
                  />
                </NavigationProvider>
              ))}
            </Accordion>
          </div>
        )
      }
      return (
        <div className="">
          <BadgesByScenario scenario={scenario} />
          {data.map((periods, i) => (
            <NavigationProvider
              // eslint-disable-next-line react/no-array-index-key
              key={i}
            >
              <ForecastDataTable
                variant={`base`}
                periods={periods.periods}
                config={periods.config}
              />
            </NavigationProvider>
          ))}
        </div>
      )
    default:
      return null
  }
}
